import classNames from "classnames";
import { AnimatePresence, motion } from "framer-motion";
import React, {
  FunctionComponent,
  MouseEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useIntl } from "react-intl";
import { Country } from "@Types/cart";
import CartCountriesList from "./CartCountriesList";
import { getCountriesByLocale } from "./countriesList";
import "./style.scss";

interface Props {
  countries: Country[];
  onSelect: (country: Country) => void;
  selectedCountry: string | undefined;
}

const CartCountrySelect: FunctionComponent<Props> = ({ countries, onSelect, selectedCountry }) => {
  const intl = useIntl();
  const selectRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [searchCountryText, setSearchCountryText] = useState<string>("");

  // HARD CODED LIST: TO REPLACE WITH API V2 CALL
  const countriesByLocale = getCountriesByLocale(intl.locale);
  if (countriesByLocale.length > 0) {
    countries = countriesByLocale;
  }

  const handleClick = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      event.stopPropagation();
      setSearchCountryText("");
      setIsOpen(!isOpen);
    },
    [isOpen, setSearchCountryText, setIsOpen]
  );

  const handleSelect = useCallback(
    (country: Country) => {
      onSelect(country);
    },
    [onSelect]
  );

  const setCountrySearch = useCallback(
    (event: KeyboardEvent) => {
      const letters = /[a-z]/i;
      if (isOpen && letters.test(event.key)) {
        setSearchCountryText((text) => `${text}${event.key}`);
      }
    },
    [isOpen, searchCountryText, setSearchCountryText]
  );

  const selectCountryByKey = useCallback(() => {
    const smallCaseKey = searchCountryText.toLowerCase();
    const targetedCountry = countries.find((country) => {
      return country.name.toLowerCase().indexOf(smallCaseKey) === 0;
    });

    if (targetedCountry) {
      handleSelect(targetedCountry);
    }
  }, [countries, handleSelect, searchCountryText]);

  useEffect(() => {
    if (searchCountryText) {
      selectCountryByKey();
    }
  }, [searchCountryText, selectCountryByKey]);

  useEffect(() => {
    const closeSelect = () => {
      setIsOpen(false);
    };

    window.addEventListener("click", closeSelect);
    window.addEventListener("keypress", setCountrySearch);

    return () => {
      window.removeEventListener("click", closeSelect);
      window.removeEventListener("keypress", setCountrySearch);
    };
  }, [setCountrySearch]);

  const selectContent =
    isOpen && selectRef.current ? (
      <motion.div
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        initial={{ opacity: 0 }}
        transition={{ delay: 0.025, duration: 0.2 }}
      >
        <CartCountriesList
          countries={countries}
          onSelect={handleSelect}
          parentPos={selectRef.current.getBoundingClientRect()}
          selectedCountry={selectedCountry}
        />
      </motion.div>
    ) : (
      []
    );

  return (
    <div
      className={classNames("CartCountrySelect", {
        "CartCountrySelect--selected": !!selectedCountry,
      })}
      onClick={handleClick}
      ref={selectRef}
      tabIndex={0}
    >
      <span className="CartCountrySelect-value">
        {selectedCountry
          ? selectedCountry
          : intl.formatMessage({ id: "cheerz_CartAddressForm_country" })}
      </span>
      <AnimatePresence>{selectContent}</AnimatePresence>
    </div>
  );
};

export default CartCountrySelect;
